import React, { useRef, useState } from 'react';

/**
 * MultiImageSlot
 * ==============
 * Multi-image variant of ReferenceUpload, used by the Product Image slot
 * inside BrandAssetsCard. Supports 1–N images (default max 2) — required
 * for video transition mode which uses two product images.
 *
 * Props:
 *   label, helpText, accent, icon, testIdPrefix  — same shape as ReferenceUpload
 *   values        — array of URLs/data URIs
 *   onChange      — (newArray: string[]) => void
 *   maxFiles      — max number of images allowed (default 2)
 *   maxSizeMB     — per-file size cap (default 6)
 */
const DEFAULT_MAX_MB = 6;

function resolveDisplayUrl(value) {
  if (!value || typeof value !== 'string') return null;
  const v = value.trim();
  if (!v) return null;
  if (v.startsWith('data:') || v.startsWith('http://') || v.startsWith('https://')) return v;
  const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  return `${base}${v.startsWith('/') ? '' : '/'}${v}`;
}

export default function MultiImageSlot({
  label,
  helpText,
  values = [],
  onChange,
  accent,
  icon,
  testIdPrefix,
  maxFiles = 2,
  maxSizeMB = DEFAULT_MAX_MB,
  addMoreLabel = null,
}) {
  const inputRef = useRef(null);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);

  const safeValues = Array.isArray(values) ? values.filter(Boolean) : [];
  const remainingSlots = Math.max(0, maxFiles - safeValues.length);

  const handleFiles = (files) => {
    setError('');
    if (!files || files.length === 0) return;
    if (remainingSlots === 0) {
      setError(`You can upload up to ${maxFiles} ${label.toLowerCase()}.`);
      return;
    }
    const accepted = Array.from(files).slice(0, remainingSlots);
    const readers = accepted.map((file) => {
      return new Promise((resolve) => {
        if (!file.type.startsWith('image/')) return resolve(null);
        if (file.size > maxSizeMB * 1024 * 1024) {
          setError(`One file exceeded ${maxSizeMB} MB and was skipped.`);
          return resolve(null);
        }
        const r = new FileReader();
        r.onloadend = () => resolve(typeof r.result === 'string' ? r.result : null);
        r.onerror = () => resolve(null);
        r.readAsDataURL(file);
      });
    });
    Promise.all(readers).then((results) => {
      const newOnes = results.filter(Boolean);
      if (newOnes.length === 0) return;
      onChange([...safeValues, ...newOnes].slice(0, maxFiles));
    });
  };

  const removeAt = (idx) => {
    onChange(safeValues.filter((_, i) => i !== idx));
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className="flex-1 min-w-[180px]" data-testid={`${testIdPrefix}-slot`}>
      <div className="flex items-center gap-1.5 mb-1">
        <span className={`inline-block w-2 h-2 rounded-full ${accent.dot}`} />
        <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">
          {label}{' '}
          <span className="text-[10px] text-slate-400 dark:text-slate-500 font-normal">
            ({safeValues.length}/{maxFiles})
          </span>
        </label>
      </div>
      {helpText && (
        <p className="text-[10px] text-slate-400 dark:text-slate-500 mb-2 leading-tight">{helpText}</p>
      )}

      {safeValues.length > 0 && (
        <div className="grid grid-cols-2 gap-2 mb-2" data-testid={`${testIdPrefix}-thumbs`}>
          {safeValues.map((val, idx) => {
            const url = resolveDisplayUrl(val);
            return (
              <div
                key={`${idx}-${val.slice(0, 24)}`}
                className={`relative rounded-lg overflow-hidden border ${accent.border} bg-slate-50 dark:bg-slate-900 aspect-square group`}
                data-testid={`${testIdPrefix}-thumb-${idx}`}
              >
                <img
                  src={url}
                  alt={`${label} ${idx + 1}`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <span className={`absolute top-1 left-1 text-[9px] font-bold px-1.5 py-0.5 rounded ${accent.badge}`}>
                  #{idx + 1}
                </span>
                <button
                  type="button"
                  onClick={() => removeAt(idx)}
                  className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-500/90 hover:bg-red-600 text-white text-xs leading-none flex items-center justify-center shadow opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`${testIdPrefix}-thumb-${idx}-remove`}
                  aria-label="Remove image"
                >
                  ×
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Empty state — full drop zone (first upload) */}
      {safeValues.length === 0 && (
        <label
          onClick={(e) => {
            // Defensive: some browsers (Chrome on Android WebView, older iOS
            // Safari, and pages where a rrweb / analytics extension has
            // intercepted the native label→input binding) don't reliably
            // bubble the click to a `display: none` file input. Manually
            // invoke .click() on the ref as a fallback — if the native
            // binding also fires this won't double-open because the input
            // is already receiving the event.
            if (e.target === e.currentTarget || e.target.tagName !== 'INPUT') {
              inputRef.current?.click();
              e.preventDefault();
            }
          }}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            handleFiles(e.dataTransfer.files);
          }}
          className={`flex flex-col items-center justify-center gap-1 cursor-pointer rounded-xl border-2 border-dashed py-3 px-2 transition-colors ${
            dragOver
              ? `${accent.border} bg-white/90 dark:bg-slate-800/80`
              : 'border-slate-300 dark:border-slate-600 bg-white/40 dark:bg-slate-800/30 hover:bg-white/70 dark:hover:bg-slate-800/50'
          }`}
        >
          <div className={`w-9 h-9 rounded-full ${accent.badge} flex items-center justify-center`}>
            {icon}
          </div>
          <span className="text-[11px] font-medium text-slate-600 dark:text-slate-300">
            Click or drop image
          </span>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple={maxFiles > 1}
            onChange={(e) => handleFiles(e.target.files)}
            className="hidden"
            data-testid={`${testIdPrefix}-upload-input`}
          />
        </label>
      )}

      {/* Once 1+ images are uploaded, the "Add more" affordance is collapsed
          behind an explicit button so users don't mistake it for a separate
          asset slot (e.g. dropping their avatar here). Tap to expand. */}
      {safeValues.length > 0 && remainingSlots > 0 && (
        <label
          onClick={(e) => {
            if (e.target === e.currentTarget || e.target.tagName !== 'INPUT') {
              inputRef.current?.click();
              e.preventDefault();
            }
          }}
          className="block cursor-pointer text-[11px] font-medium rounded-lg border border-dashed border-slate-300 dark:border-slate-600 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:border-slate-400 py-1.5 text-center"
          data-testid={`${testIdPrefix}-add-more`}
          title={addMoreLabel || "Add another image"}
        >
          {addMoreLabel || `+ Add another ${label.toLowerCase()} (up to ${maxFiles})`}
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple={maxFiles > 1}
            onChange={(e) => handleFiles(e.target.files)}
            className="hidden"
            data-testid={`${testIdPrefix}-upload-input`}
          />
        </label>
      )}

      {error && (
        <p className="mt-1 text-[10px] text-red-500" data-testid={`${testIdPrefix}-error`}>
          {error}
        </p>
      )}
    </div>
  );
}
